import React from 'react';
import ClaimCard from './ClaimCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SearchResults = ({ results, loading, searchQuery, totalResults, currentPage, onPageChange }) => {
  const resultsPerPage = 10;
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="h-6 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
              <div className="h-8 bg-muted rounded-full w-24"></div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
              <div className="h-4 bg-muted rounded w-4/6"></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-4 bg-muted rounded w-32"></div>
              <div className="flex space-x-2">
                <div className="h-8 w-8 bg-muted rounded"></div>
                <div className="h-8 w-8 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!results || results?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {searchQuery ? 'No results found' : 'Start your search'}
        </h3>
        <p className="text-muted-foreground mb-6">
          {searchQuery 
            ? `No claims found for "${searchQuery}". Try different keywords or adjust your filters.`
            : 'Enter a claim, topic, or paste a link to begin verification.'
          }
        </p>
        {searchQuery && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Suggestions:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {["COVID-19", "Climate change", "Election 2024", "Healthcare", "Economy"]?.map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Handle suggestion click
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">
            Search Results
          </h2>
          <span className="text-sm text-muted-foreground">
            {totalResults?.toLocaleString()} results found
            {searchQuery && ` for "${searchQuery}"`}
          </span>
        </div>

        {/* Sort Options */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <select className="text-sm border border-border rounded px-2 py-1 bg-card text-foreground">
            <option value="relevance">Relevance</option>
            <option value="date">Date</option>
            <option value="confidence">Confidence</option>
            <option value="popularity">Popularity</option>
          </select>
        </div>
      </div>
      {/* Results List */}
      <div className="space-y-4">
        {results?.map((claim) => (
          <ClaimCard key={claim?.id} claim={claim} />
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 pt-8">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </Button>

          <div className="flex items-center space-x-1">
            {[...Array(Math.min(5, totalPages))]?.map((_, index) => {
              const pageNum = currentPage <= 3 
                ? index + 1 
                : currentPage + index - 2;
              
              if (pageNum > totalPages) return null;
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className="w-10"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
      {/* Results Info */}
      <div className="text-center text-sm text-muted-foreground">
        Showing {((currentPage - 1) * resultsPerPage) + 1} to {Math.min(currentPage * resultsPerPage, totalResults)} of {totalResults?.toLocaleString()} results
      </div>
    </div>
  );
};

export default SearchResults;